import * as Popover from '@radix-ui/react-popover';
import dayjs from 'dayjs';
import { Calendar } from 'lucide-react';
import { forwardRef, useState } from 'react';
import { DateRangePicker as ReactDateRangePicker, StaticRange } from 'react-date-range';
// Import react-date-range CSS files
import 'react-date-range/dist/styles.css';
import 'react-date-range/dist/theme/default.css';
// Import our SCSS module
import { Button } from '@common/index';
import styles from './styles.module.scss';

import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';

dayjs.extend(utc);
dayjs.extend(timezone);
// 日期工具函数
const getStartOfDay = (date: Date): Date => {
    return dayjs(date).startOf('day').toDate();
};

const getEndOfDay = (date: Date): Date => {
    return dayjs(date).endOf('day').toDate();
};

const getStartOfWeek = (date: Date): Date => {
    return dayjs(date).startOf('week').toDate();
};

const getEndOfWeek = (date: Date): Date => {
    return dayjs(date).endOf('week').toDate();
};

const getStartOfMonth = (date: Date): Date => {
    return dayjs(date).startOf('month').toDate();
};

const getEndOfMonth = (date: Date): Date => {
    return dayjs(date).endOf('month').toDate();
};

const getStartOfQuarter = (date: Date): Date => {
    const month = dayjs(date).month();
    const quarterStartMonth = Math.floor(month / 3) * 3;
    return dayjs(date).month(quarterStartMonth).startOf('month').toDate();
};

const getEndOfQuarter = (date: Date): Date => {
    const month = dayjs(date).month();
    const quarterStartMonth = Math.floor(month / 3) * 3;
    return dayjs(date).month(quarterStartMonth + 2).endOf('month').toDate();
};

const getStartOfYear = (date: Date): Date => {
    return dayjs(date).startOf('year').toDate();
};

const getEndOfYear = (date: Date): Date => {
    return dayjs(date).endOf('year').toDate();
};


const StaticRanges: StaticRange[] = [
    {
        label: 'Today',
        range: () => ({
            startDate: getStartOfDay(dayjs().toDate()),
            endDate: getEndOfDay(dayjs().toDate()),
        }),
        isSelected: (range) =>
            dayjs(range.startDate).isSame(dayjs(), 'day')
                && dayjs(range.endDate).isSame(dayjs(), 'day'),
    },
    {
        label: 'Yesterday',
        range: () => ({
            startDate: getStartOfDay(dayjs().subtract(1, 'day').toDate()),
            endDate: getEndOfDay(dayjs().subtract(1, 'day').toDate()),
        }),
        isSelected: (range) =>
            dayjs(range.startDate).isSame(dayjs().subtract(1, 'day'), 'day')
                && dayjs(range.endDate).isSame(dayjs().subtract(1, 'day'), 'day'),
    },
    {
        label: 'Last 7 Days',
        range: () => ({
            startDate: dayjs().subtract(7, 'day').toDate(),
            endDate: dayjs().toDate(),
        }),
        isSelected: (range) =>
            dayjs(range.startDate).isSame(dayjs().subtract(7, 'day'), 'day')
                && dayjs(range.endDate).isSame(dayjs(), 'day'),
    },
    {
        label: 'Last 30 Days',
        range: () => ({
            startDate: dayjs().subtract(30, 'day').toDate(),
            endDate: dayjs().toDate(),
        }),
        isSelected: (range) =>
            dayjs(range.startDate).isSame(dayjs().subtract(30, 'day'), 'day')
                && dayjs(range.endDate).isSame(dayjs(), 'day'),
    },
    {
        label: 'Last 90 Days',
        range: () => ({
            startDate: dayjs().subtract(90, 'day').toDate(),
            endDate: dayjs().toDate(),
        }),
        isSelected: (range) =>
            dayjs(range.startDate).isSame(dayjs().subtract(90, 'day'), 'day')
                && dayjs(range.endDate).isSame(dayjs(), 'day'),
    },
    {
        label: 'Last 365 Days',
        range: () => ({
            startDate: dayjs().subtract(365, 'day').toDate(),
            endDate: dayjs().toDate(),
        }),
        isSelected: (range) =>
            dayjs(range.startDate).isSame(dayjs().subtract(365, 'day'), 'day')
                && dayjs(range.endDate).isSame(dayjs(), 'day'),
    },
    {
        label: 'Last 12 Months',
        range: () => ({
            startDate: getStartOfDay(dayjs().subtract(11, 'month').startOf('month').toDate()),
            endDate: getEndOfDay(dayjs().toDate()),
        }),
        isSelected: (range) =>
            dayjs(range.startDate).isSame(dayjs().subtract(11, 'month').startOf('month'), 'day')
                && dayjs(range.endDate).isSame(dayjs(), 'day'),
    },
    {
        label: 'Last Week',
        range: () => ({
            startDate: getStartOfWeek(dayjs().subtract(1, 'week').toDate()),
            endDate: getEndOfWeek(dayjs().subtract(1, 'week').toDate()),
        }),
        isSelected: (range) =>
            dayjs(range.startDate).isSame(getStartOfWeek(dayjs().subtract(1, 'week').toDate()), 'day')
                && dayjs(range.endDate).isSame(getEndOfWeek(dayjs().subtract(1, 'week').toDate()), 'day'),
    },
    {
        label: 'Last Month',
        range: () => ({
            startDate: getStartOfMonth(dayjs().subtract(1, 'month').toDate()),
            endDate: getEndOfMonth(dayjs().subtract(1, 'month').toDate()),
        }),
        isSelected: (range) =>
            dayjs(range.startDate).isSame(getStartOfMonth(dayjs().subtract(1, 'month').toDate()), 'day')
                && dayjs(range.endDate).isSame(getEndOfMonth(dayjs().subtract(1, 'month').toDate()), 'day'),
    },
    {
        label: 'Last Quarter',
        range: () => {
            const lastQuarter = dayjs().subtract(3, 'month');
            return {
                startDate: getStartOfQuarter(lastQuarter.toDate()),
                endDate: getEndOfQuarter(lastQuarter.toDate()),
            };
        },
        isSelected: (range) => {
            const lastQuarter = dayjs().subtract(3, 'month');
            return dayjs(range.startDate).isSame(getStartOfQuarter(lastQuarter.toDate()), 'day')
                    && dayjs(range.endDate).isSame(getEndOfQuarter(lastQuarter.toDate()), 'day');
        },
    },
    {
        label: 'Last Year',
        range: () => ({
            startDate: getStartOfYear(dayjs().subtract(1, 'year').toDate()),
            endDate: getEndOfYear(dayjs().subtract(1, 'year').toDate()),
        }),
        isSelected: (range) =>
            dayjs(range.startDate).isSame(getStartOfYear(dayjs().subtract(1, 'year').toDate()), 'day')
                && dayjs(range.endDate).isSame(getEndOfYear(dayjs().subtract(1, 'year').toDate()), 'day'),
    },
];

export interface DateRange {
  startDate: Date | undefined;
  endDate: Date | undefined;
}

export interface DateRangePickerProps {
  value: DateRange;
  onConfirm?: (range: DateRange) => void;
  placeholder?: string;
  className?: string;
}

export const DateRangeDisplay = forwardRef<HTMLButtonElement, DateRangePickerProps & React.HTMLAttributes<HTMLButtonElement>>(({
    value, className = '', placeholder = '', ...props
}, ref) =>{
    const formatDateRange = () => {
        const label = StaticRanges.find((item) => item.isSelected(value))?.label || '';
        if (label) {
            return label;
        }

        if (value?.startDate && value?.endDate) {
            return `${dayjs(value.startDate).format('MMM DD,YYYY')} - ${dayjs(value.endDate).format('MMM DD,YYYY')}`;
        }
        return placeholder;
    };

    return (
        <button
            ref={ref}
            className={`${styles.display} ${className} ${
                value?.startDate ? styles.hasValue : styles.placeholder
            }`}
            {...props}
        >
            <Calendar size={18} />
            {formatDateRange()}
        </button>
    );
});
DateRangeDisplay.displayName = 'DateRangeDisplay';


export const DateRangePicker = ({
    value,
    onConfirm,
    placeholder = 'Please select a date range',
    className = '',
}: DateRangePickerProps) => {
    const [isOpen, setIsOpen] = useState(false);
    const [date, setDate] = useState<DateRange>(value || { startDate: null, endDate: null });

    // Convert value to react-date-range format
    const ranges = date?.startDate && date?.endDate ? [
        {
            startDate: date.startDate,
            endDate: date.endDate,
            key: 'selection'
        }
    ] : [];

    const handleRangeChange = (ranges: any) => {
        const selection = ranges.selection;
        if (selection.startDate && selection.endDate) {
            setDate({
                startDate: selection.startDate,
                endDate: getEndOfDay(selection.endDate),
            });
        }
    };

    return (
        <Popover.Root
            open={isOpen}
            onOpenChange={(open) => {
                setIsOpen(open);
                if (!open) {
                    setDate(value || { startDate: null, endDate: null });
                }
            }}
        >
            <Popover.Trigger asChild>
                <DateRangeDisplay
                    value={value}
                    className={styles.trigger}
                    placeholder={placeholder}
                />
            </Popover.Trigger>

            <Popover.Portal>
                <Popover.Content
                    className={styles.popoverContent}
                    sideOffset={5}
                    align="center"
                >
                    <div className={styles.container}>
                        {/* React Date Range Picker */}
                        <div className={styles.dateRangeContainer}>
                            <ReactDateRangePicker
                                onChange={handleRangeChange}
                                ranges={ranges}
                                months={2}
                                direction="horizontal"
                                rangeColors={['#1A162F']}
                                staticRanges={StaticRanges}
                            />
                            <div className={styles.confirmButtonContainer}>
                                <Button 
                                    variant="outline"
                                    color="gray"
                                    onClick={() => {
                                        setIsOpen(false);
                                        setDate(value || { startDate: null, endDate: null });
                                    }}
                                >
                                    Cancel
                                </Button>
                                <Button
                                    onClick={() => {
                                        onConfirm?.(date);
                                        setIsOpen(false);
                                    }}
                                >
                                    Confirm
                                </Button>
                            </div>
                        </div>
                    </div>
                </Popover.Content>
            </Popover.Portal>
        </Popover.Root>
    );
};